/**
 * PartnerProfile — permission-gated section inside Settings.
 *
 * Two tabs:
 *   Profile      — displays the partner's business profile (read-only, edit via Settings)
 *   Attribution  — controls the partner branding footer shown on public invitations
 *
 * Authorization: Settings controls visibility with MANAGE_PARTNER_PROFILE.
 * Attribution tab: only actionable with MANAGE_PARTNER_ATTRIBUTION permission.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { DashboardHeader } from '@/src/modules/components/Header';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';
import { createProfile, updateProfile, fetchAttribution, updateAttribution } from '@/src/modules/partner/api';
import type { AttributionSettings, UpdateAttributionPayload } from '@/src/modules/partner/types';
import type { PartnerProfileSnapshot } from '@/src/modules/auth/session';

type Tab = 'profile' | 'attribution';

export function PartnerProfile({ embedded = false }: { embedded?: boolean }) {
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const partnerProfile = useAuthStore((s) => s.partnerProfile);
  const permissions = useAuthStore((s) => s.permissions);
  const canManageAttribution = permissions.includes('MANAGE_PARTNER_ATTRIBUTION');

  return (
    <div className={embedded ? '' : 'min-h-screen bg-surface'}>
      {!embedded && <DashboardHeader title="Partner Profile" />}

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-on-surface/10">
          <TabButton active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
            Business Profile
          </TabButton>
          {canManageAttribution && partnerProfile && (
            <TabButton active={activeTab === 'attribution'} onClick={() => setActiveTab('attribution')}>
              Attribution
            </TabButton>
          )}
        </div>

        {/* Tab content */}
        {activeTab === 'profile' && <ProfileTab profile={partnerProfile} />}
        {activeTab === 'attribution' && canManageAttribution && partnerProfile && <AttributionTab />}
      </div>
    </div>
  );
}

// ── Tab button ────────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-on-surface-variant hover:text-on-surface',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ── Profile Tab ───────────────────────────────────────────────────────────────

const PARTNER_TYPES = [
  { value: 'PHOTOGRAPHER', label: 'Photographer' },
  { value: 'EVENT_ORGANIZER', label: 'Event Organizer' },
  { value: 'WEDDING_PLANNER', label: 'Wedding Planner' },
  { value: 'DECORATOR', label: 'Decorator' },
  { value: 'OTHER', label: 'Other' },
];

function CreateProfileForm() {
  const loadCurrentUser = useAuthStore((s) => s.loadCurrentUser);
  const [businessName, setBusinessName] = useState('');
  const [partnerType, setPartnerType] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [phone, setPhone] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!businessName.trim() || !partnerType) return;
    setBusy(true);
    setError(null);
    try {
      await createProfile({
        businessName: businessName.trim(),
        partnerType,
        ...(ownerName.trim() ? { ownerName: ownerName.trim() } : {}),
        ...(legalName.trim() ? { legalName: legalName.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(websiteUrl.trim() ? { websiteUrl: websiteUrl.trim() } : {}),
        ...(googleMapsUrl.trim() ? { googleMapsUrl: googleMapsUrl.trim() } : {}),
        ...(logoUrl.trim() ? { logoUrl: logoUrl.trim() } : {}),
      });
      await loadCurrentUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile.');
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-on-surface/10 bg-surface-container p-6">
      <h3 className="text-base font-semibold text-on-surface mb-1">Set up your partner profile</h3>
      <p className="text-sm text-on-surface-variant mb-5">
        Tell us about your business so clients can find you.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1">
            Business name <span className="text-status-failed">*</span>
          </label>
          <input
            type="text"
            required
            maxLength={255}
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="e.g. Shutter & Bloom Studio"
            className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1">
            Business type <span className="text-status-failed">*</span>
          </label>
          <select
            required
            value={partnerType}
            onChange={(e) => setPartnerType(e.target.value)}
            className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="" disabled>Select a type…</option>
            {PARTNER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1">
            Owner name <span className="text-on-surface-variant/40">(optional)</span>
          </label>
          <input
            type="text"
            maxLength={255}
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Your full name"
            className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1">
            Legal/Registered Business name <span className="text-on-surface-variant/40">(optional)</span>
          </label>
          <input
            type="text"
            maxLength={255}
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            placeholder="Legal business name"
            className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1">
            Phone <span className="text-on-surface-variant/40">(optional)</span>
          </label>
          <input
            type="tel"
            maxLength={50}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
            className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1">
            Website URL <span className="text-on-surface-variant/40">(optional)</span>
          </label>
          <input
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://example.com"
            className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1">
            Google Maps URL <span className="text-on-surface-variant/40">(optional)</span>
          </label>
          <input
            type="url"
            value={googleMapsUrl}
            onChange={(e) => setGoogleMapsUrl(e.target.value)}
            placeholder="https://maps.google.com/..."
            className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1">
            Logo URL <span className="text-on-surface-variant/40">(optional)</span>
          </label>
          <input
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
            className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        {error && <p className="text-xs text-status-failed">{error}</p>}
        <button
          type="submit"
          disabled={busy || !businessName.trim() || !partnerType}
          className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none"
        >
          {busy ? 'Creating…' : 'Create partner profile'}
        </button>
      </form>
    </div>
  );
}

function ProfileTab({
  profile,
}: {
  profile: PartnerProfileSnapshot | null;
}) {
  const loadCurrentUser = useAuthStore((s) => s.loadCurrentUser);
  const [isEditing, setIsEditing] = useState(false);
  const [businessName, setBusinessName] = useState(profile?.businessName ?? '');
  const [partnerType, setPartnerType] = useState(profile?.partnerType ?? '');
  const [ownerName, setOwnerName] = useState(profile?.ownerName ?? '');
  const [legalName, setLegalName] = useState(profile?.legalName ?? '');
  const [phone, setPhone] = useState(profile?.phone ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(profile?.websiteUrl ?? '');
  const [googleMapsUrl, setGoogleMapsUrl] = useState(profile?.googleMapsUrl ?? '');
  const [logoUrl, setLogoUrl] = useState(profile?.logoUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setBusinessName(profile.businessName);
      setPartnerType(profile.partnerType);
      setOwnerName(profile.ownerName ?? '');
      setLegalName(profile.legalName ?? '');
      setPhone(profile.phone ?? '');
      setWebsiteUrl(profile.websiteUrl ?? '');
      setGoogleMapsUrl(profile.googleMapsUrl ?? '');
      setLogoUrl(profile.logoUrl ?? '');
    }
  }, [profile]);

  if (!profile) {
    return <CreateProfileForm />;
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!businessName.trim() || !partnerType) return;
    setBusy(true);
    setError(null);
    try {
      await updateProfile({
        businessName: businessName.trim(),
        partnerType,
        ownerName: ownerName.trim() || null,
        legalName: legalName.trim() || null,
        phone: phone.trim() || null,
        websiteUrl: websiteUrl.trim() || null,
        googleMapsUrl: googleMapsUrl.trim() || null,
        logoUrl: logoUrl.trim() || null,
      });
      await loadCurrentUser();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile.');
    } finally {
      setBusy(false);
    }
  }

  if (isEditing) {
    return (
      <div className="rounded-2xl border border-on-surface/10 bg-surface-container p-6">
        <h3 className="text-base font-semibold text-on-surface mb-1">Edit Business Profile</h3>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-on-surface-variant mb-1">
              Business name <span className="text-status-failed">*</span>
            </label>
            <input
              type="text"
              required
              maxLength={255}
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-on-surface-variant mb-1">
              Business type <span className="text-status-failed">*</span>
            </label>
            <select
              required
              value={partnerType}
              onChange={(e) => setPartnerType(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {PARTNER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-on-surface-variant mb-1">
              Owner name <span className="text-on-surface-variant/40">(optional)</span>
            </label>
            <input
              type="text"
              maxLength={255}
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-on-surface-variant mb-1">
              Legal/Registered Business name <span className="text-on-surface-variant/40">(optional)</span>
            </label>
            <input
              type="text"
              maxLength={255}
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-on-surface-variant mb-1">
              Phone <span className="text-on-surface-variant/40">(optional)</span>
            </label>
            <input
              type="tel"
              maxLength={50}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-on-surface-variant mb-1">
              Website URL <span className="text-on-surface-variant/40">(optional)</span>
            </label>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-on-surface-variant mb-1">
              Google Maps URL <span className="text-on-surface-variant/40">(optional)</span>
            </label>
            <input
              type="url"
              value={googleMapsUrl}
              onChange={(e) => setGoogleMapsUrl(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-on-surface-variant mb-1">
              Logo URL <span className="text-on-surface-variant/40">(optional)</span>
            </label>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/50 bg-surface px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          {error && <p className="text-xs text-status-failed">{error}</p>}
          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={busy || !businessName.trim() || !partnerType}
              className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none"
            >
              {busy ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setIsEditing(false)}
              className="flex-1 py-2.5 rounded-xl border border-on-surface/10 bg-surface text-on-surface text-sm font-semibold disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-on-surface/10 bg-surface-container divide-y divide-on-surface/10">
        <ProfileRow label="Business Name" value={profile.businessName} />
        <ProfileRow label="Partner Type" value={formatPartnerType(profile.partnerType)} />
        {profile.ownerName && <ProfileRow label="Owner Name" value={profile.ownerName} />}
        {profile.legalName && <ProfileRow label="Legal Name" value={profile.legalName} />}
        {profile.phone && <ProfileRow label="Phone" value={profile.phone} />}
        {profile.websiteUrl && <ProfileRow label="Website" value={<a href={profile.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{profile.websiteUrl}</a>} />}
        {profile.googleMapsUrl && <ProfileRow label="Location" value={<a href={profile.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">View on Google Maps</a>} />}
        {profile.logoUrl && <ProfileRow label="Logo URL" value={<a href={profile.logoUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{profile.logoUrl}</a>} />}
        <ProfileRow label="Status" value={<StatusBadge status={profile.status} />} />
        <ProfileRow label="Onboarding" value={formatOnboardingStep(profile.onboardingStep)} />
      </div>
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className="w-full py-2.5 rounded-xl border border-on-surface/10 bg-surface text-on-surface text-sm font-semibold hover:bg-surface-low"
      >
        Edit profile
      </button>
    </div>
  );
}

function ProfileRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-6 py-4">
      <span className="text-sm text-on-surface-variant">{label}</span>
      <span className="text-sm font-medium text-on-surface text-right break-all ml-4">{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colour =
    status === 'ACTIVE'
      ? 'bg-green-100 text-green-800'
      : status === 'PENDING'
        ? 'bg-yellow-100 text-yellow-800'
        : status === 'SUSPENDED'
          ? 'bg-red-100 text-red-800'
          : 'bg-neutral-100 text-neutral-600';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colour}`}>
      {status}
    </span>
  );
}

function formatPartnerType(type: string | null | undefined) {
  if (!type) return '';
  return type
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

function formatOnboardingStep(step: string | null | undefined) {
  if (!step) return 'Not Started';
  return step
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}


// ── Attribution Tab ───────────────────────────────────────────────────────────

function AttributionTab() {
  const [settings, setSettings] = useState<AttributionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAttribution();
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load attribution settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function save(patch: UpdateAttributionPayload) {
    if (!settings) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await updateAttribution(patch);
      setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save attribution settings.');
    } finally {
      setSaving(false);
    }
  }

  function toggle(field: keyof UpdateAttributionPayload, value: boolean) {
    void save({ [field]: value });
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-on-surface/10 bg-surface-container divide-y divide-on-surface/10">
        <ToggleRow
          label="Show attribution footer"
          description="Display your business branding on public invitation pages."
          checked={settings.enabled}
          onChange={(v) => toggle('enabled', v)}
          disabled={saving}
        />
        <ToggleRow
          label="Show logo"
          description="Display your business logo in the footer."
          checked={settings.displayLogo}
          onChange={(v) => toggle('displayLogo', v)}
          disabled={saving || !settings.enabled}
        />
        <ToggleRow
          label="Show phone number"
          description="Display your contact number in the footer."
          checked={settings.displayPhone}
          onChange={(v) => toggle('displayPhone', v)}
          disabled={saving || !settings.enabled}
        />
        <ToggleRow
          label="Show website"
          description="Display your website link in the footer."
          checked={settings.displayWebsite}
          onChange={(v) => toggle('displayWebsite', v)}
          disabled={saving || !settings.enabled}
        />
        <ToggleRow
          label="Show location"
          description="Display a link to your Google Maps location."
          checked={settings.displayLocation}
          onChange={(v) => toggle('displayLocation', v)}
          disabled={saving || !settings.enabled}
        />
      </div>

      {/* Custom message */}
      <div className="rounded-2xl border border-on-surface/10 bg-surface-container p-6 space-y-3">
        <div>
          <p className="text-sm font-medium text-on-surface">Custom tagline</p>
          <p className="text-xs text-on-surface-variant mt-0.5">
            A short tagline shown below your business name (e.g. "Capturing Memories Forever").
          </p>
        </div>
        <CustomMessageField
          value={settings.customMessage ?? ''}
          disabled={saving || !settings.enabled}
          onSave={(msg) => void save({ customMessage: msg })}
        />
      </div>

      {/* Status feedback */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      {saved && (
        <p className="text-sm text-green-600">Settings saved.</p>
      )}
    </div>
  );
}

// ── Toggle row ────────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className={`flex items-start justify-between px-6 py-4 gap-4 ${disabled ? 'opacity-50' : ''}`}>
      <div>
        <p className="text-sm font-medium text-on-surface">{label}</p>
        <p className="text-xs text-on-surface-variant mt-0.5">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={[
          'relative flex-shrink-0 h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          checked ? 'bg-primary' : 'bg-on-surface/20',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        ].join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

// ── Custom message field ──────────────────────────────────────────────────────

function CustomMessageField({
  value,
  disabled,
  onSave,
}: {
  value: string;
  disabled: boolean;
  onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const dirty = draft !== value;

  useEffect(() => { setDraft(value); }, [value]);

  return (
    <div className="flex gap-2">
      <input
        type="text"
        value={draft}
        disabled={disabled}
        maxLength={500}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="e.g. Capturing Memories Forever"
        className="flex-1 rounded-lg border border-on-surface/20 bg-surface px-3 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
      />
      {dirty && (
        <button
          disabled={disabled}
          onClick={() => onSave(draft)}
          className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-on-primary hover:opacity-90 disabled:opacity-50"
        >
          Save
        </button>
      )}
    </div>
  );
}
